import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

const EMPTY = { category_name: "", description: "" };

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

export default function ProductCategories({ embedded = false }) {
  const { isReadOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = async () => {
    const nextItems = (await api.get("/categories")).data;
    setItems(nextItems);
    setSelectedIds((current) => current.filter((id) => nextItems.some((item) => item.id === id)));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditId(null);
    setForm(EMPTY);
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditId(it.id);
    setForm({ category_name: it.category_name || "", description: it.description || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (editId) await api.patch(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Category diperbarui" : "Category ditambahkan");
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Category dihapus");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      return false;
    }
  };

  const doConfirmDel = async () => {
    if (!confirmDel) return;
    if (confirmDel.ids) {
      try {
        const response = await api.post("/categories/bulk-delete", { ids: confirmDel.ids });
        toast.success(`Category dihapus: ${response.data.deleted}`);
        setSelectedIds([]);
        setConfirmDel(null);
        load();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
      return;
    }
    if (await remove(confirmDel.id)) setConfirmDel(null);
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((itemId) => itemId !== id));
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/categories/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Import category berhasil");
      event.target.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="product-categories-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {!embedded && <div>
          <div className="label-mini">Product</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Category</h1>
        </div>}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm" onClick={() => downloadBlob("/categories/template", "category-template.xlsx")} data-testid="download-category-template"><Download className="h-4 w-4 mr-2" />Template</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadBlob("/categories/export", "categories.xlsx")} data-testid="export-category-excel"><FileSpreadsheet className="h-4 w-4 mr-2" />Export Excel</Button>
          <label className="inline-flex cursor-pointer items-center rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent">
            <Upload className="h-4 w-4 mr-2" />Import Excel
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} data-testid="import-category-file" />
          </label>
          {!isReadOnly && items.length > 0 && (
            <div className="flex basis-full items-center gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setSelectedIds(allSelected ? [] : items.map((item) => item.id))} data-testid="toggle-select-all-categories">
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
              {selectedIds.length > 0 && (
                <Button type="button" variant="destructive" size="sm" className="rounded-sm" onClick={() => setConfirmDel({ ids: selectedIds })} data-testid="bulk-delete-categories">
                  <Trash2 className="h-4 w-4 mr-2" />Hapus {selectedIds.length} Category
                </Button>
              )}
            </div>
          )}
          {!isReadOnly && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" onClick={openNew} data-testid="new-category-button"><Plus className="h-4 w-4 mr-2" />Category Baru</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle>{editId ? "Edit Category" : "Category Baru"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                  <div className="md:col-span-4">
                    <Label className="label-mini">Category Name</Label>
                    <Input required value={form.category_name} onChange={(e) => setForm({ ...form, category_name: e.target.value })} data-testid="category-name-input" />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="label-mini">Description</Label>
                    <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description-input" />
                  </div>
                  <div className="md:col-span-4 flex gap-2">
                    <Button type="submit" className="rounded-sm" data-testid="save-category-button">{editId ? "Update" : "Tambah"}</Button>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); resetForm(); }} data-testid="cancel-category-edit">Batal</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="w-12 px-6 py-3">
                {!isReadOnly && <Checkbox checked={allSelected} onCheckedChange={(checked) => setSelectedIds(checked ? items.map((item) => item.id) : [])} aria-label="Pilih semua category" data-testid="select-all-categories" />}
              </th>
              <th className="text-left px-6 py-3">No</th>
              <th className="text-left px-6 py-3">Category Name</th>
              <th className="text-left px-6 py-3">Description</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Belum ada category.</td></tr>}
            {pageItems.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`category-row-${it.id}`}>
                <td className="px-6 py-3"><Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={(checked) => toggleSelected(it.id, checked)} aria-label={`Pilih ${it.category_name}`} data-testid={`select-category-${it.id}`} /></td>
                <td className="px-6 py-3 font-medium">{it.no ?? "-"}</td>
                <td className="px-6 py-3">{it.category_name}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.description || "-"}</td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  {!isReadOnly && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-category-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`delete-category-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix="category-pg" />
      </div>

      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.category_name}
        testIdPrefix="confirm-delete-category"
      />
    </div>
  );
}
